<template>
  <div class="faq-container">
    <!-- 返回按钮 -->
    <div class="back-button-container">
      <button @click="goBack" class="back-button">
        <span class="back-arrow">←</span> 返回首页
      </button>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>加载中...</p>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <p>加载失败：{{ error }}</p>
      <button @click="fetchContentUrl" class="retry-button">重试</button>
    </div>

    <!-- 内容预览区域 -->
    <div v-else-if="contentUrl" class="content-container">
      <!-- 图片预览 -->
      <img 
        v-if="isImage" 
        :src="contentUrl" 
        alt="常见问题" 
        class="image-viewer"
      />
      
      <!-- PDF 预览 -->
      <iframe 
        v-else-if="isPdf" 
        :src="contentUrl" 
        title="常见问题" 
        class="pdf-viewer"
        frameborder="0"
      ></iframe>
    </div>

    <!-- 无内容状态 -->
    <div v-else class="empty-container">
      <p>没有找到相关内容</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

const router = useRouter();
const route = useRoute();
const { locale } = useI18n();

// 内容地址
const contentUrl = ref('');
// 加载状态
const loading = ref(false);
// 错误信息
const error = ref('');

// 返回首页函数
const goBack = () => {
  router.push('/');
};

// 检查是否是图片
const isImage = computed(() => {
  if (!contentUrl.value) return false;
  const url = contentUrl.value.toLowerCase();
  return url.endsWith('.jpg') || url.endsWith('.jpeg') || url.endsWith('.png') || url.endsWith('.gif') || url.endsWith('.webp');
});

// 检查是否是PDF
const isPdf = computed(() => {
  if (!contentUrl.value) return false;
  return contentUrl.value.toLowerCase().endsWith('.pdf');
});

// 处理URL字符串，去除多余的空格和反引号
const processUrl = (url: string): string => {
  return url.replace(/^[\s`]+|[\s`]+$/g, '');
};

// 获取内容URL的函数
const fetchContentUrl = async () => {
  const menuId = route.query.menuId;
  
  if (!menuId) {
    error.value = '没有获取到菜单ID';
    return;
  }
  
  loading.value = true;
  error.value = '';
  
  try {
    // 调用接口获取内容URL，使用菜单id作为参数
    const response = await fetch(`/jeecg-boot/energycube/menu/queryMenuDetail?id=${menuId}`);
    const data = await response.json();
    
    if (data.code === 200 && data.result && data.result.length > 0) {
      // 根据当前语言选择对应的URL
      const currentLang = locale.value;
      const item = data.result[0];
      
      // 选择对应的URL
      let url = '';
      if (currentLang === 'zh-Hant') {
        url = processUrl(item.imgUrl);
      } else {
        url = processUrl(item.imgUrlen);
      }
      
      contentUrl.value = url;
    } else {
      error.value = '获取内容链接失败';
    }
  } catch (err) {
    console.error('获取内容链接失败:', err);
    error.value = '网络错误，请稍后重试';
  } finally {
    loading.value = false;
  }
};

// 监听语言变化，重新获取内容
watch(locale, () => {
  if (route.query.menuId) {
    fetchContentUrl();
  }
});

// 组件挂载时获取内容URL
onMounted(() => {
  fetchContentUrl();
});
</script>

<style scoped>
.faq-container {
  width: 100%;
  min-height: 100vh;
  background-color: #000000;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 50px 20px 20px;
  box-sizing: border-box;
  margin-top: 100px;
}

.back-button-container {
  width: 100%;
  max-width: 1200px;
  margin-bottom: 20px;
}

.back-button {
  display: flex;
  align-items: center;
  background-color: #12aa4a;
  color: white;
  border: none;
  padding: 12px 24px;
  font-size: 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.back-button:hover {
  background-color: #0e8e3e;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.back-arrow {
  margin-right: 8px;
  font-size: 18px;
}

.content-container {
  width: 80%;
  max-width: 800px;
  min-height: 600px;
  background-color: rgba(0, 0, 0, 0.8);
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  margin: 20px 0;
}

.pdf-viewer {
  width: 100%;
  height: 80vh;
  background-color: white;
}

.image-viewer {
  width: 100%;
  height: auto;
  object-fit: contain;
  border-radius: 0;
}

.empty-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  width: 100%;
  color: #999;
}

/* 加载状态样式 */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  width: 100%;
  color: white;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  border-top-color: #12aa4a;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 错误状态样式 */
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  width: 100%;
  color: #ff4d4f;
}

.retry-button {
  margin-top: 16px;
  padding: 6px 16px;
  background-color: #12aa4a;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.retry-button:hover {
  background-color: #0e8e3e;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .faq-container {
    padding: 15px;
  }

  .back-button {
    padding: 10px 20px;
    font-size: 14px;
  }

  .pdf-container {
    height: calc(100vh - 100px);
  }
}
</style>
